// src/utils/errorLogger.js
// *******************************************************************************
// ERROR LOGGER UTILITY
// *******************************************************************************
// Global helper to log errors in a standardized and visible way
// Use in all catch blocks to never have silent errors again
// *******************************************************************************

/**
 * Logs an error in a visible and standardized way to the console
 *
 * @param {Error|string} error - Error object or error message
 * @param {object} options - Additional options
 * @param {string} options.context - Context where the error occurred (e.g., "ResetPassword", "authService.signIn")
 * @param {object} options.extraData - Extra data for debugging (e.g., { userId, email })
 * @param {boolean} options.silent - If true, doesn't show decorative borders (useful for less critical errors)
 *
 * @example
 * // Basic usage
 * try {
 *   await someAsyncFunction();
 * } catch (error) {
 *   logError(error);
 * }
 *
 * @example
 * // Usage with context
 * try {
 *   await authService.signIn(credentials);
 * } catch (error) {
 *   logError(error, { context: 'Login.handleSubmit' });
 * }
 *
 * @example
 * // Usage with extra data
 * try {
 *   await api.updateUser(userId, data);
 * } catch (error) {
 *   logError(error, {
 *     context: 'UserProfile.save',
 *     extraData: { userId, data }
 *   });
 * }
 */
export function logError(error, options = {}) {
  const { context = 'Unknown', extraData = null, silent = false } = options;

  if (!silent) {
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.error(
      `%c🚨 ERROR CAUGHT: ${context}`,
      'color: #ff0000; font-weight: bold; font-size: 16px; background: #ffebee; padding: 8px;'
    );
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }

  // Show error message
  const errorMessage = error?.message || error?.toString?.() || String(error);
  console.error('%cMessage:', 'font-weight: bold; color: #ff6b6b;', errorMessage);

  // Show error name (TypeError, ReferenceError, etc)
  if (error?.name) {
    console.error('%cType:', 'font-weight: bold;', error.name);
  }

  // Show stack trace if available
  if (error?.stack) {
    console.error('%cStack Trace:', 'font-weight: bold; color: #ff6b6b;');
    console.error(error.stack);
  }

  // Show extra data if provided
  if (extraData) {
    console.error('%cExtra Data:', 'font-weight: bold; color: #4caf50;');
    console.error(extraData);
  }

  // Show complete error object
  if (error && typeof error === 'object') {
    console.error('%cComplete Error Object:', 'font-weight: bold;');
    console.error(error);
  }

  if (!silent) {
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }

  // TODO: Add integration with Sentry, Datadog, etc
  // sendToSentry(error, { context, extraData });
}

/**
 * Silent version of logError (without decorative borders)
 * Useful for expected or less critical errors
 */
export function logErrorSilent(error, options = {}) {
  return logError(error, { ...options, silent: true });
}

/**
 * Logs a warning (not an error, but something suspicious)
 */
export function logWarning(message, options = {}) {
  const { context = 'Unknown', extraData = null } = options;

  console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.warn(
    `%c⚠️ WARNING: ${context}`,
    'color: #ff9800; font-weight: bold; font-size: 14px; background: #fff3e0; padding: 6px;'
  );
  console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.warn('%cMessage:', 'font-weight: bold; color: #ff9800;', message);

  if (extraData) {
    console.warn('%cData:', 'font-weight: bold;');
    console.warn(extraData);
  }

  console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  logError,
  logErrorSilent,
  logWarning,
};
